import { PaneBackButton } from "@/components/PaneBackButton";
import { PaneExpandButton } from "@/components/PaneExpandButton";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS,
  CANVAS_DETAIL_PANE_HEADER_CLASS,
} from "@/panes/canvasDetailPaneHeaderClasses";
import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import {
  FileRecordTypeIcon,
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutHeader,
  PaneLayoutHeaderActions,
} from "@cohesive/ui-components";
import { useFlags } from "launchdarkly-react-client-sdk";
import { X } from "lucide-react";
import { lazy, Suspense } from "react";
import {
  AudioContent,
  BlockContent,
  FallbackContent,
  HtmlContent,
  ImageContent,
  MarkdownContent,
  OfficeContent,
  PdfContent,
  SpreadsheetContent,
  TextContent,
  VideoContent,
} from "./content";
import { classifyFileRecord, type FileRecordContentKind } from "./contentType";
import { EditableFileRecordName } from "./EditableFileRecordName";
import { FileRecordExportProvider } from "./FileRecordExportContext";
import { FileRecordHistoryTriggerProvider } from "./FileRecordHistoryTriggerContext";
import { FileRecordPaneHeaderActions } from "./FileRecordPaneHeaderActions";
import { HtmlAutosaveIndicator } from "./HtmlAutosaveIndicator";
import { HtmlInlineEditProvider } from "./HtmlInlineEditContext";
import { LoadingOverlay } from "./LoadingOverlay";
import { fileRecordEligibleForApiVersioning } from "./versioning/FileRecordVersioningContext";

const NotionLikeCollaborativeEditor = lazy(() =>
  import("./content/NotionLikeCollaborativeEditor").then((mod) => ({
    default: mod.NotionLikeCollaborativeEditor,
  })),
);

export type FileRecordDetailPaneViewProps = {
  record: FileRecord;
  showPaneClose?: boolean;
  onClose: () => void;
};

function FileRecordDetailPaneViewInner({
  record,
  showPaneClose = true,
  onClose,
}: FileRecordDetailPaneViewProps) {
  const flags = useFlags();

  const kind: FileRecordContentKind = classifyFileRecord(record);
  const isNotionMarkdown = kind === "markdown" && flags.notionLikeEditor;
  // The server reports the user's edit capability (parent-derived OR direct
  // editor share). `undefined` means "unknown": only GET-by-id and
  // shared-with-me populate it, while list hooks and mutation responses
  // (publish/rename/recover/…) reseed this record's per-id cache WITHOUT it.
  // We default unknown to ALLOWED (fail-open) — flipping it closed makes
  // editor actions vanish intermittently whenever one of those writes lands
  // while the pane is open. The backend enforces the real permission, so this
  // is a UX default only. (To safely fail closed, those cache writes must
  // first preserve can_edit/can_manage via a merge instead of a full replace.)
  const canEdit = record.can_edit ?? true;
  // Lifecycle (rename here) requires parent-scope edit — a direct share is
  // content-only. Same fail-open default for the same cache-clobber reason.
  const canManage = record.can_manage ?? true;
  // Version revert is an editor-only mutation, so the history UI is gated too.
  // HTML records share the same content-type-agnostic version API + preview
  // path as notion markdown; they were only ever excluded by this gate.
  const apiVersioningEnabled =
    (isNotionMarkdown || kind === "html") &&
    fileRecordEligibleForApiVersioning(record) &&
    canEdit;

  const isFullBleed =
    kind === "image" ||
    kind === "pdf" ||
    kind === "video" ||
    kind === "html" ||
    kind === "office" ||
    kind === "spreadsheet" ||
    kind === "markdown";

  const renderContent = () => {
    switch (kind) {
      case "image":
        return <ImageContent record={record} />;
      case "pdf":
        return <PdfContent record={record} />;
      case "video":
        return <VideoContent record={record} />;
      case "audio":
        return <AudioContent record={record} />;
      case "markdown":
        if (flags.notionLikeEditor) {
          return (
            <Suspense
              fallback={
                <div className="relative h-full w-full overflow-hidden">
                  <LoadingOverlay />
                </div>
              }
            >
              <NotionLikeCollaborativeEditor record={record} />
            </Suspense>
          );
        }
        return <MarkdownContent record={record} />;
      case "html":
        // A block is authored for a fixed box — fit it, never reflow it.
        // Canvas-resident blocks land here too: double-click and Open route
        // them to the pane, and the lightbox stays their full-size surface.
        if (record.display?.mode === "block") {
          // Keyed by record: the block card holds content frozen while its
          // edit session lives (so refetches never yank a mid-edit document),
          // and every other surface mounts one card per record. Reusing the
          // instance across block→block navigation would show the previous
          // document frozen inside the next record's stage.
          return <BlockContent key={record.id} record={record} />;
        }
        return <HtmlContent record={record} />;
      case "text":
        return <TextContent record={record} />;
      case "office":
        return <OfficeContent record={record} />;
      case "spreadsheet":
        return <SpreadsheetContent record={record} />;
      case "fallback":
      default:
        return (
          <FallbackContent
            record={record}
            onOpenExternal={
              record.url
                ? () =>
                    window.open(record.url!, "_blank", "noopener,noreferrer")
                : undefined
            }
          />
        );
    }
  };

  const headerActions = (
    <>
      <PaneBackButton />
      <div className="flex min-w-0 flex-1 items-center gap-1.5 pl-1">
        <FileRecordTypeIcon
          contentType={record.content_type}
          originType={record.origin_type}
          className="h-4 w-4 shrink-0"
        />
        {/* Renaming is a lifecycle action — gate on canManage, not canEdit, so
            a content-only shared editor sees a read-only name. */}
        <EditableFileRecordName record={record} canEdit={canManage} />
      </div>
      <TooltipProvider>
        {kind === "html" && canEdit && <HtmlAutosaveIndicator />}
        <FileRecordPaneHeaderActions
          record={record}
          canEdit={canEdit}
          canManage={canManage}
          isNotionMarkdown={isNotionMarkdown}
          apiVersioningEnabled={apiVersioningEnabled}
        />
        <PaneExpandButton />

        {showPaneClose && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="toolbarGhost"
                size="icon-sm"
                className="shrink-0"
                onClick={onClose}
                aria-label="Close"
              >
                <X />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Close</TooltipContent>
          </Tooltip>
        )}
      </TooltipProvider>
    </>
  );

  const pane = (
    <PaneLayout>
      <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
        <PaneLayoutHeaderActions
          className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
        >
          {headerActions}
        </PaneLayoutHeaderActions>
      </PaneLayoutHeader>
      <PaneLayoutContent
        className={cn(isFullBleed && "p-0")}
        noScroll={isFullBleed}
      >
        {renderContent()}
      </PaneLayoutContent>
    </PaneLayout>
  );

  return pane;
}

/**
 * Pure shell + body for a resolved `FileRecord` (no data fetching).
 */
export function FileRecordDetailPaneView(props: FileRecordDetailPaneViewProps) {
  return (
    <FileRecordExportProvider>
      <HtmlInlineEditProvider>
        <FileRecordHistoryTriggerProvider>
          <FileRecordDetailPaneViewInner {...props} />
        </FileRecordHistoryTriggerProvider>
      </HtmlInlineEditProvider>
    </FileRecordExportProvider>
  );
}
